import React from 'react';
import { motion } from 'framer-motion';
import { Gem, Info, MessageCircle, Sparkles } from 'lucide-react';
import { GoldRule } from './GoldRule';
import { BRIDAL_IMAGES, WHATSAPP_URL } from './data';
import { useBooking } from './BookingContext';
import { useLanguage } from './i18n/LanguageContext';
import { getBridalCopy } from './i18n/bridalCopy';
export function Bridal() {
  const {
    lang
  } = useLanguage();
  const {
    openBooking
  } = useBooking();
  const copy = getBridalCopy(lang);
  const whatsappHref = `${WHATSAPP_URL}?text=${encodeURIComponent(copy.whatsappMessage)}`;
  return <section id="novias" className="max-w-2xl mx-auto px-6 pt-16 scroll-mt-24" data-id="element-91" data-mp="1j65bva3022cl">
      <GoldRule data-id="element-92" data-mp="1f53b2otfpi8i">{copy.rule}</GoldRule>

      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.7
    }} className="relative bg-cream border border-gold-ultra-light p-7 sm:p-11" data-id="element-93" data-mp="1f64w9m45zl3y">
        <span aria-hidden className="pointer-events-none absolute inset-3 border border-gold/15" data-id="element-94" data-mp="yyppzchrxbd8" />

        <div className="relative z-10" data-id="element-95" data-mp="13z54469vqmw9">
          <div className="flex items-center gap-3 mb-4" data-id="element-96" data-mp="13vzi4zwjs14r">
            <Gem className="w-5 h-5 text-gold" strokeWidth={1.5} data-id="element-97" data-mp="pzvxnybh775h" />
            <p className="font-body font-semibold text-[10px] tracking-[5px] uppercase text-gold" data-id="element-98" data-mp="tzmn9yp9nh8f">
              {copy.eyebrow}
            </p>
          </div>

          <h2 className="font-serif font-light text-dark text-3xl sm:text-[38px] leading-[1.15] mb-4" data-id="element-99" data-mp="1hrtnxa20yadn">
            {copy.title}
          </h2>

          <p className="font-body font-light text-mid text-sm leading-[1.85] italic" data-id="element-100" data-mp="18y8bvyh8v77x">
            {copy.subtitle}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3" data-id="element-101" data-mp="1cxh3awotat0b">
            {[{
            src: BRIDAL_IMAGES.makeup,
            alt: copy.imageAltMakeup
          }, {
            src: BRIDAL_IMAGES.updo,
            alt: copy.imageAltUpdo
          }].map((image, i) => <motion.div key={image.src} initial={{
            clipPath: 'inset(0 0 100% 0)'
          }} whileInView={{
            clipPath: 'inset(0 0 0% 0)'
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 1,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.1 * i
          }} className="group relative overflow-hidden bg-cream aspect-[3/4]" data-id="element-102" data-mp="2fbbjffwtw2i2" data-mpi={i}>
                <img src={image.src} alt={image.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" data-id="element-103" data-mp="1wcpd9mmgfl7a" />
                <span aria-hidden className="pointer-events-none absolute inset-2.5 border border-warm-white/45" data-id="element-104" data-mp="1ilkm78x5k18k" />
              </motion.div>)}
          </div>

          {/* Paquetes principales */}
          <div className="mt-12" data-id="element-105" data-mp="18oy24mcnhe7l">
            <div className="flex items-center gap-4 mb-6" data-id="element-106" data-mp="yjg0oukj3s7t">
              <span aria-hidden className="h-px w-8 bg-gold/45" data-id="element-107" data-mp="yl0w0meo3f6q" />
              <h3 className="font-body font-semibold text-[10px] tracking-[5px] uppercase text-gold" data-id="element-108" data-mp="13gzmcfjk379s">
                {copy.packagesTitle}
              </h3>
              <span aria-hidden className="h-px flex-1 bg-gold/20" data-id="element-109" data-mp="1dg75jjcz1w4t" />
            </div>

            <ul className="space-y-3.5" data-id="element-110" data-mp="1ddumf2vh9fq6">
              {copy.packages.map((pkg, i) => <motion.li key={pkg.name} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.55,
              delay: 0.07 * i
            }} className="bg-warm-white border border-brand-border/70 p-6 transition-colors duration-500 hover:border-gold/45" data-id="element-111" data-mp="fqc6n83efdco" data-mpi={i}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2" data-id="element-112" data-mp="20ihsprw8t2y">
                    <h4 className="font-serif font-light text-dark text-xl sm:text-[22px] leading-tight" data-id="element-113" data-mp="23po74znn30va">
                      {pkg.name}
                    </h4>
                    <p className="flex items-baseline gap-1.5 whitespace-nowrap" data-id="element-114" data-mp="1ypa27qhc471h">
                      {pkg.priceNote && <span className="font-body font-light text-soft text-[10px] tracking-[2px] uppercase" data-id="element-115" data-mp="28osjfz5v1epu">
                          {pkg.priceNote}
                        </span>}
                      <span className="font-serif font-medium text-champagne-deep text-[22px] leading-none" data-id="element-116" data-mp="2ifvjcxn1znnj">
                        {pkg.price}
                      </span>
                    </p>
                  </div>
                  <p className="mt-3 pt-3 border-t border-brand-border/50 font-body font-light text-mid text-xs leading-[1.75]" data-id="element-117" data-mp="2rh3gvdhqwkgk">
                    {pkg.desc}
                  </p>
                </motion.li>)}
            </ul>
          </div>

          {/* Servicios adicionales */}
          <div className="mt-12" data-id="element-118" data-mp="38q5t9jcbvpy9">
            <div className="flex items-center gap-4 mb-3" data-id="element-119" data-mp="38r7cwc7etkrl">
              <span aria-hidden className="h-px w-8 bg-gold/45" data-id="element-120" data-mp="3dn5wznlpggm7" />
              <h3 className="font-body font-semibold text-[10px] tracking-[5px] uppercase text-gold" data-id="element-121" data-mp="2yzkt3rh0add8">
                {copy.addOnsTitle}
              </h3>
              <span aria-hidden className="h-px flex-1 bg-gold/20" data-id="element-122" data-mp="33wl0q6ti7o6g" />
            </div>

            <p className="font-body font-light text-soft text-xs leading-[1.7] italic mb-5" data-id="element-123" data-mp="2pxtmmken62ib">
              {copy.addOnsSubtitle}
            </p>

            <ul data-id="element-124" data-mp="2g0gp4x4bn9sf">
              {copy.addOns.map(addOn => <li key={addOn.name} className="flex items-baseline justify-between gap-4 py-3.5 border-b border-brand-border/50 last:border-b-0" data-id="element-125" data-mp="2g207ct9jfuwu">
                  <span className="flex items-baseline gap-2.5 font-body font-light text-dark text-sm tracking-[0.3px]" data-id="element-126" data-mp="e9vgll3rz3np">
                    <Sparkles className="w-3 h-3 text-gold-light flex-shrink-0 translate-y-0.5" strokeWidth={1.75} aria-hidden data-id="element-127" data-mp="j5syhvuosi2l" />
                    {addOn.name}
                  </span>
                  <span className="font-serif font-medium text-champagne-deep text-base whitespace-nowrap" data-id="element-128" data-mp="7tc17okbew7f">
                    {addOn.price}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Nota informativa */}
          <div className="mt-10 flex items-start gap-3 bg-champagne-pale/60 border border-gold-ultra-light p-5" data-id="element-129" data-mp="vi4rb4e9a9u4">
            <Info className="w-4 h-4 text-champagne-deep flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden data-id="element-130" data-mp="qqd9pjd2veg4" />
            <p className="font-body font-light text-mid text-[11px] leading-[1.8]" data-id="element-131" data-mp="mpkzamsgz3i9">
              {copy.note}
            </p>
          </div>

          {/* Llamadas a la acción */}
          <div className="mt-9 flex flex-col items-center gap-3.5" data-id="element-132" data-mp="1eg7k7u9xcqx7">
            <motion.button type="button" onClick={openBooking} whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1]
          }} className="metallic-reserve-button inline-flex w-full items-center justify-center gap-3 px-9 py-4 text-white font-body font-semibold text-[10px] tracking-[3px] uppercase outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream sm:w-auto sm:px-11" data-id="element-133" data-mp="2x4a6fhuhnv3g">
              <Gem className="w-4 h-4" strokeWidth={1.75} aria-hidden data-id="element-134" data-mp="3kzmnlk1t8hss" />
              {copy.ctaPrimary}
            </motion.button>

            <motion.a href={whatsappHref} target="_blank" rel="noreferrer" whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-flex w-full items-center justify-center gap-3 border border-gold/50 px-9 py-4 text-gold font-body font-semibold text-[10px] tracking-[3px] uppercase transition-colors duration-300 hover:bg-gold hover:text-warm-white outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream sm:w-auto sm:px-11" data-id="element-135" data-mp="3tusz8znrhu38">
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} aria-hidden data-id="element-136" data-mp="3jmjxend02tsz" />
              {copy.ctaSecondary}
            </motion.a>
          </div>
        </div>
      </motion.div>
    </section>;
}